/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useState, useEffect, useCallback } from "react";
import { apiUrl } from "@/utils/apiUrl";
import { formatDistanceMi } from "@/utils/distance";
import {
  getCachedNearby,
  setCachedNearby,
  getCachedPlaceDetails,
  setCachedPlaceDetails,
} from "@/utils/placesCache";

export interface SOSPlace {
  name: string;
  distance: string;
  distanceKm: number;
  phone: string;
  lat: number;
  lng: number;
  /** When true, card renders as a hardcoded emergency action (tel:911 etc). */
  isFallback?: boolean;
  /** Fallback only — href used by the tap-to-call / maps-search button. */
  fallbackHref?: string;
}

export interface SOSPlaces {
  hospitals: SOSPlace[];
  police: SOSPlace[];
  pharmacies: SOSPlace[];
  /** True if the hook surfaced hardcoded 911 / 311 / "find hospital" cards
      because both the client-side Maps JS API and /api/places returned 0 results. */
  usedFallback: boolean;
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => deg * (Math.PI / 180);
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Bug 3: Google's `type=hospital` nearby search returns many non-emergency
// "health" facilities — chiropractors, dentists, optometrists, physical
// therapists, vets, med spas — because Google tags them loosely. A nearby
// chiropractor (e.g. "Smith Chiropractic") was sorting to #1 under "Nearest
// Hospital" in the SOS screen. For a life-safety list we want real
// hospitals / ER / urgent care only.
//
// Strategy (type-aware, not just name-based):
//   1. If Google's `types[]` explicitly tags the place `hospital`, keep it —
//      that's Google's reliable signal for an actual hospital.
//   2. Otherwise, allow it ONLY if the NAME positively reads as emergency care
//      (hospital / medical center / ER / urgent care / etc.).
//   3. Always drop places whose types or name indicate a non-emergency
//      specialty, even if Google returned them under the hospital search.
const HOSPITAL_NAME_INCLUDE_RE =
  /\b(hospital|medical cent(?:er|re)|med(?:ical)? cent(?:er|re)|emergency|e\.?r\.?|urgent care|trauma|health (?:system|center)|clinic|infirmary)\b/i;

// Non-emergency specialties to exclude (name match). Catches the reported
// chiropractor plus the common offenders Google mixes into type=hospital.
const HOSPITAL_NAME_EXCLUDE_RE =
  /\b(chiropract(?:ic|or)|dental|dentist|orthodont|optometr|optical|eye care|veterinar|animal|physical therapy|physiotherap|spa|wellness|massage|cosmetic|dermatolog|psychiatr|psycholog|counsel(?:l)?ing|therap(?:y|ist)|fertility|dialysis|rehab|nursing home|hospice|associates|solutions|insurance|medicaid|consulting|bureau)\b/i;

// Google place types that are clearly NOT an emergency hospital — if any of
// these is present, drop the result regardless of name.
const HOSPITAL_EXCLUDE_TYPES = new Set([
  "dentist",
  "veterinary_care",
  "physiotherapist",
  "spa",
  "beauty_salon",
  "insurance_agency",
]);

// For `type=police` we only want real departments / sheriffs — not police
// academies, private security firms, ancillary offices, etc.
const POLICE_INCLUDE_RE = /\b(police|sheriff|trooper|state patrol|highway patrol|constable|marshal)\b/i;

/**
 * Decide whether a `type=hospital` nearby-search result is a real emergency
 * hospital / ER / urgent care worth showing in the SOS list. Pure + exported
 * so the safety-critical filtering is unit-tested.
 *
 * @param name   place display name
 * @param types  Google Places `types[]` for the result (may be missing)
 */
export function isEmergencyHospital(
  name: string,
  types: readonly string[] = [],
): boolean {
  if (!name) return false;
  const typeSet = new Set(types);
  // Hard exclude by type (a dentist/vet tagged hospital is still not an ER).
  for (const t of HOSPITAL_EXCLUDE_TYPES) {
    if (typeSet.has(t)) return false;
  }
  // Hard exclude by name specialty (catches the reported chiropractor).
  if (HOSPITAL_NAME_EXCLUDE_RE.test(name)) return false;
  // Keep if Google explicitly types it a hospital.
  if (typeSet.has("hospital")) return true;
  // Otherwise require a positive emergency-care name signal.
  return HOSPITAL_NAME_INCLUDE_RE.test(name);
}

export function passesCategoryFilter(
  type: string,
  name: string,
  types: readonly string[] = [],
): boolean {
  if (!name) return false;
  if (type === "hospital") return isEmergencyHospital(name, types);
  if (type === "police") return POLICE_INCLUDE_RE.test(name);
  return true;
}

// Upstream slice size — we pull more results than we keep so the category
// filter has room to drop bad matches before we pick the nearest 3.
const CANDIDATE_POOL = 20;
const FINAL_COUNT = 3;

// ────────────────────────────────────────────────────────────────────────────
// Client-side path (web): uses the Maps JS PlacesService directly
// ────────────────────────────────────────────────────────────────────────────

interface RawResult {
  place: SOSPlace;
  placeId: string;
}

function searchCategoryClient(
  service: google.maps.places.PlacesService,
  location: { lat: number; lng: number },
  type: string,
): Promise<RawResult[]> {
  return new Promise((resolve) => {
    service.nearbySearch(
      { location, radius: 5000, type },
      (results, status) => {
        if (status !== "OK" || !results) { resolve([]); return; }

        const mapped: RawResult[] = results
          .slice(0, CANDIDATE_POOL)
          .filter((r) => passesCategoryFilter(type, r.name ?? "", r.types ?? []))
          .map((r) => {
            const placeLat = r.geometry?.location?.lat() || 0;
            const placeLng = r.geometry?.location?.lng() || 0;
            const km = haversineKm(location.lat, location.lng, placeLat, placeLng);
            return {
              place: {
                name: r.name || "Unknown",
                distance: formatDistanceMi(km),
                distanceKm: km,
                phone: "",
                lat: placeLat,
                lng: placeLng,
              },
              placeId: r.place_id || "",
            };
          });

        mapped.sort((a, b) => a.place.distanceKm - b.place.distanceKm);
        resolve(mapped.slice(0, FINAL_COUNT));
      },
    );
  });
}

function fetchPhoneClient(
  service: google.maps.places.PlacesService,
  raw: RawResult,
): Promise<SOSPlace> {
  if (!raw.placeId) return Promise.resolve(raw.place);
  return new Promise((resolve) => {
    service.getDetails(
      { placeId: raw.placeId, fields: ["formatted_phone_number", "international_phone_number"] },
      (detail, status) => {
        if (status === "OK" && detail) {
          resolve({
            ...raw.place,
            phone: detail.formatted_phone_number || detail.international_phone_number || "",
          });
        } else {
          resolve(raw.place);
        }
      },
    );
  });
}

async function fetchViaClient(lat: number, lng: number): Promise<SOSPlaces> {
  const div = document.createElement("div");
  const service = new window.google.maps.places.PlacesService(div);
  const location = { lat, lng };

  const [hospitalRaw, policeRaw, pharmacyRaw] = await Promise.all([
    searchCategoryClient(service, location, "hospital"),
    searchCategoryClient(service, location, "police"),
    searchCategoryClient(service, location, "pharmacy"),
  ]);

  const [hospitals, police, pharmacies] = await Promise.all([
    Promise.all(hospitalRaw.map((r) => fetchPhoneClient(service, r))),
    Promise.all(policeRaw.map((r) => fetchPhoneClient(service, r))),
    Promise.all(pharmacyRaw.map((r) => fetchPhoneClient(service, r))),
  ]);

  return { hospitals, police, pharmacies };
}

// ────────────────────────────────────────────────────────────────────────────
// Server-side path (native): proxies through /api/places + /api/place-details
// ────────────────────────────────────────────────────────────────────────────

async function searchCategoryServer(
  lat: number,
  lng: number,
  type: string,
): Promise<RawResult[]> {
  const url = apiUrl(`/api/places?lat=${lat}&lng=${lng}&type=${encodeURIComponent(type)}&radius=5000`);
  try {
    let data: any = getCachedNearby<any>(lat, lng, type);
    if (!data) {
      const res = await fetch(url);
      if (!res.ok) {
        console.error("[useSOSPlaces] /api/places non-OK", {
          type,
          status: res.status,
          url,
        });
        return [];
      }
      data = await res.json();
      setCachedNearby(lat, lng, type, data);
    }
    const results: unknown[] = Array.isArray(data?.results) ? data.results : [];
    if (results.length === 0) {
      console.warn("[useSOSPlaces] /api/places returned 0 results for", type, {
        googleStatus: data?.status,
        error: data?.error_message,
      });
    }

    const mapped: RawResult[] = results
      .slice(0, CANDIDATE_POOL)
      .filter((raw) => {
        const r = raw as Record<string, any>;
        return passesCategoryFilter(type, r.name ?? "", r.types ?? []);
      })
      .map((raw) => {
        const r = raw as Record<string, any>;
        const placeLat: number = r.geometry?.location?.lat ?? 0;
        const placeLng: number = r.geometry?.location?.lng ?? 0;
        const km = haversineKm(lat, lng, placeLat, placeLng);
        return {
          place: {
            name: r.name || "Unknown",
            distance: formatDistanceMi(km),
            distanceKm: km,
            phone: "",
            lat: placeLat,
            lng: placeLng,
          },
          placeId: r.place_id || "",
        };
      });

    mapped.sort((a, b) => a.place.distanceKm - b.place.distanceKm);
    return mapped.slice(0, FINAL_COUNT);
  } catch (err) {
    console.error("[useSOSPlaces] /api/places fetch error", { type, url, err });
    return [];
  }
}

async function fetchPhoneServer(raw: RawResult): Promise<SOSPlace> {
  if (!raw.placeId) return raw.place;
  const cached = getCachedPlaceDetails<{ phone?: string }>(raw.placeId);
  if (cached) {
    return { ...raw.place, phone: cached.phone || "" };
  }
  try {
    const res = await fetch(apiUrl(`/api/place-details?placeId=${encodeURIComponent(raw.placeId)}`));
    if (!res.ok) return raw.place;
    const data = (await res.json()) as { phone?: string };
    setCachedPlaceDetails(raw.placeId, data);
    return { ...raw.place, phone: data.phone || "" };
  } catch {
    return raw.place;
  }
}

async function fetchViaServer(lat: number, lng: number): Promise<Omit<SOSPlaces, "usedFallback">> {
  const [hospitalRaw, policeRaw, pharmacyRaw] = await Promise.all([
    searchCategoryServer(lat, lng, "hospital"),
    searchCategoryServer(lat, lng, "police"),
    searchCategoryServer(lat, lng, "pharmacy"),
  ]);

  const [hospitals, police, pharmacies] = await Promise.all([
    Promise.all(hospitalRaw.map(fetchPhoneServer)),
    Promise.all(policeRaw.map(fetchPhoneServer)),
    Promise.all(pharmacyRaw.map(fetchPhoneServer)),
  ]);

  return { hospitals, police, pharmacies };
}

// ────────────────────────────────────────────────────────────────────────────
// Hardcoded emergency fallback
//   Shown when BOTH the Maps JS client path and /api/places return 0 results.
//   Always gives the user a working action: 911, police non-emergency, and
//   "find nearest hospital" in Google Maps.
// ────────────────────────────────────────────────────────────────────────────

function emergencyFallback(lat: number, lng: number): SOSPlaces {
  const mapsHospitalUrl =
    `https://www.google.com/maps/search/?api=1&query=hospital&query_place_id=&ll=${lat},${lng}`;

  const mk = (name: string, phone: string, fallbackHref: string): SOSPlace => ({
    name,
    distance: "",
    distanceKm: 0,
    phone,
    lat,
    lng,
    isFallback: true,
    fallbackHref,
  });

  return {
    hospitals: [
      mk("Call 911 (Emergency)", "911", "tel:911"),
      mk("Find Nearest Hospital", "", mapsHospitalUrl),
    ],
    police: [
      mk("Call 911 (Emergency)", "911", "tel:911"),
      mk("Call Police Non-Emergency", "311", "tel:311"),
    ],
    pharmacies: [],
    usedFallback: true,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────────────────

export function useSOSPlaces(lat: number | null, lng: number | null) {
  const [places, setPlaces] = useState<SOSPlaces>({
    hospitals: [],
    police: [],
    pharmacies: [],
    usedFallback: false,
  });
  const [loading, setLoading] = useState(false);

  const doFetch = useCallback(async () => {
    // Strict null/undefined check — `0` is a valid lat/lng (equator / prime
    // meridian) so truthy-guards would incorrectly skip those locations.
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      return;
    }
    setLoading(true);

    try {
      // Prefer client-side if the Maps JS API loaded; otherwise use the
      // server-side proxy (works from Capacitor native).
      const hasGoogleMaps = !!(window as any).google?.maps?.places?.PlacesService;
      const result = hasGoogleMaps
        ? await fetchViaClient(lat, lng)
        : await fetchViaServer(lat, lng);

      const totalResults =
        result.hospitals.length + result.police.length + result.pharmacies.length;

      if (totalResults === 0) {
        console.warn("[useSOSPlaces] 0 results — showing hardcoded fallback", {
          hasGoogleMaps,
          lat,
          lng,
        });
        setPlaces(emergencyFallback(lat, lng));
      } else {
        setPlaces({ ...result, usedFallback: false });
      }
    } catch (err) {
      console.error("[useSOSPlaces] fetch failed, showing fallback:", err);
      setPlaces(emergencyFallback(lat, lng));
    } finally {
      setLoading(false);
    }
  }, [lat, lng]);

  useEffect(() => { doFetch(); }, [doFetch]);

  return { places, loading };
}
